import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { ToggleSwitch } from 'primeng/toggleswitch'
import { ButtonDirective } from 'primeng/button'
import { InputText } from 'primeng/inputtext'
import { debounceTime, distinctUntilChanged, take } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ContentUnitStateService } from '../../services/content-unit-state.service'
import { TemplateStateService } from '../../services/template-state.service'
import { InsuredSidebarStateService } from '../../services/insured-sidebar-state.service'
import { ContentUnitService } from '@app/features/content-unit/services/content-unit.service'
import { TemplateService } from '@app/features/template/services/template.service'
import { SIDEBAR_CONTENT_TYPE } from '../../models/sidebar.constants'
import { SidebarFilter } from '../../models/sidebar-filter.model'
import { IconField } from 'primeng/iconfield'
import { InputIcon } from 'primeng/inputicon'
import { AutoComplete } from 'primeng/autocomplete'

@Component({
  selector: 'albert-listview-filter',
  standalone: true,
  templateUrl: './list-view-filter.component.html',
  imports: [
    ToggleSwitch,
    ButtonDirective,
    InputText,
    ReactiveFormsModule,
    IconField,
    InputIcon,
    AutoComplete
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListViewFilterComponent {
  public readonly contentType = input<string>(SIDEBAR_CONTENT_TYPE.INSURED)
  public readonly isFilterSupported = computed(() => {
    return this.contentType() === SIDEBAR_CONTENT_TYPE.CONTENT_UNITS || this.contentType() === SIDEBAR_CONTENT_TYPE.TEMPLATES
  })
  public readonly filterForm = new FormGroup({
    q: new FormControl(''),
    tags: new FormControl<string[]>([]),
    includeContent: new FormControl(false)
  })
  public readonly suggestedTags = signal<string[]>([])
  protected readonly sidebarContentType = SIDEBAR_CONTENT_TYPE
  private readonly contentUnitState = inject(ContentUnitStateService)
  private readonly templateState = inject(TemplateStateService)
  private readonly sidebarState = inject(InsuredSidebarStateService)
  private readonly contentUnitService = inject(ContentUnitService)
  private readonly templateService = inject(TemplateService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly activeStateService = computed(() => {
    switch (this.contentType()) {
      case SIDEBAR_CONTENT_TYPE.CONTENT_UNITS:
        return this.contentUnitState
      case SIDEBAR_CONTENT_TYPE.TEMPLATES:
        return this.templateState
      case SIDEBAR_CONTENT_TYPE.INSURED:
        return this.sidebarState
      default:
        return null
    }
  })

  constructor() {
    this.filterForm.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      takeUntilDestroyed()
    ).subscribe(value => {
      this.activeStateService()?.setFilter(this.getCleanFilter(value))
    })

    effect(() => {
      const service = this.activeStateService()
      if (service) {
        // When switching views, ensure the current filter is applied to the new service
        // We use untracked to avoid re-triggering this effect when the form value changes,
        // as form changes are already handled by the valueChanges subscription above.
        const currentFilter = untracked(() => this.getCleanFilter(this.filterForm.value))
        service.setFilter(currentFilter)
      }
    })
  }

  public onTagsSearch(event: { query: string }): void {
    const query = event.query
    const tagService = this.contentType() === SIDEBAR_CONTENT_TYPE.TEMPLATES
      ? this.templateService
      : this.contentUnitService

    tagService.getTags(query).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(tags => this.suggestedTags.set(tags))
  }

  public reset(): void {
    this.filterForm.reset({
      q: '',
      tags: [],
      includeContent: false
    })
  }

  private getCleanFilter(value: Partial<{
    q: string | null;
    tags: string[] | null;
    includeContent: boolean | null
  }>): SidebarFilter {
    const filter: SidebarFilter = { q: value.q ?? '' }
    if (this.contentType() === SIDEBAR_CONTENT_TYPE.CONTENT_UNITS) {
      filter.tags = value.tags ?? []
      filter.includeContent = value.includeContent ?? false
    }
    return filter
  }
}
